import React, { useState, useMemo, useRef } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useReadingContextSafe } from '../../contexts/ReadingContext';
import { useMessages } from '../../hooks/useMessages';
import { useShopping } from '../../hooks/useShopping';
import { format, addHours, isAfter } from 'date-fns';
import { getActiveShoppingItems, getRecentActiveShoppingItems, isActiveShoppingItem } from '../../utils/shoppingHelpers';
import { 
  Calendar, ShoppingCart, MessageSquare, DollarSign, 
  ChevronRight, AlertCircle, Clock, Users, Star,
  CheckCircle, ArrowRight, Eye, Plus, Sparkles,
  MapPin, Target, Waves, Book, BookOpen, Heart, 
  Bike, Coffee, Gamepad2, Settings, RotateCcw, Search, Bot
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import UnifiedBig3Planner from '../Planning/UnifiedBig3Planner';
import ActivityRatingSlider from '../Family/ActivityRatingSlider';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import { parseShoppingItem } from '../../utils/smartItemParser';
import ReadingChallengeWidget from './ReadingChallengeWidget';
import CollegeJourneyWidget from '../College/CollegeJourneyWidget';
import WhatsNewSection from './WhatsNewSection';
import NotificationSetup from '../Shared/NotificationSetup';
import NotificationTester from '../Admin/NotificationTester';
import './SmartDashboard.css';

// Helper to determine time of day context
const getTimeContext = () => {
  const hour = new Date().getHours();
  
  if (hour >= 5 && hour < 9) return { period: 'morning', greeting: 'Good morning', focus: 'start' };
  if (hour >= 9 && hour < 12) return { period: 'mid-morning', greeting: 'Good morning', focus: 'active' };
  if (hour >= 12 && hour < 14) return { period: 'lunch', greeting: 'Good afternoon', focus: 'break' };
  if (hour >= 14 && hour < 17) return { period: 'afternoon', greeting: 'Good afternoon', focus: 'active' };
  if (hour >= 17 && hour < 19) return { period: 'evening', greeting: 'Good evening', focus: 'wind-down' };
  if (hour >= 19 && hour < 22) return { period: 'night', greeting: 'Good evening', focus: 'family' };
  return { period: 'late-night', greeting: 'Hello', focus: 'rest' };
};

// Main Smart Dashboard Component
export default function SmartDashboard({ isMobile, breakpoint } = {}) {
  const { 
    family, 
    currentUser, 
    familyCalendar, 
    familyActivities,
    familyTimeInvites,
    plans,
    allowanceJobs,
    setCurrentUser,
    user,
    familyUsers,
    switchUser
  } = useFamilyContext();
  
  // Reading context for kids only
  const isKid = !currentUser?.isParent;
  const readingContext = useReadingContextSafe();
  const { 
    currentBook, 
    readingStats, 
    activeChallenge, 
    isLoading: readingLoading 
  } = readingContext || {};
  
  const { messages } = useMessages({ recipientId: currentUser?.id });
  const { 
    items: shoppingItems,
    addItem: addShoppingItem
  } = useShopping();
  
  const navigate = useNavigate();
  
  // Shopping quick add state
  const [shoppingQuickAdd, setShoppingQuickAdd] = useState('');
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isUrgentItemsExpanded, setIsUrgentItemsExpanded] = useState(false);
  const shoppingInputRef = useRef(null);
  
  const timeContext = getTimeContext();
  const now = new Date();
  const today = format(now, 'yyyy-MM-dd');
  
  // Calculate shopping stats - filter out completed/purchased/deleted items
  const activeShoppingItems = getActiveShoppingItems(shoppingItems);
  const pendingShoppingItems = activeShoppingItems;
  const recentShoppingItems = getRecentActiveShoppingItems(shoppingItems, 3);
  
  // Handle shopping quick add
  const handleShoppingQuickAdd = async (e) => {
    e.preventDefault();
    if (!shoppingQuickAdd.trim() || isAddingItem) return;
    
    setIsAddingItem(true);
    try {
      const parsedItem = parseShoppingItem(shoppingQuickAdd);
      await addShoppingItem(parsedItem);
      setShoppingQuickAdd('');
      // Refocus input after adding
      setTimeout(() => shoppingInputRef.current?.focus(), 100);
    } catch (err) {
      console.error('Error adding item:', err);
    } finally {
      setIsAddingItem(false);
    }
  };
  
  // Enhanced event filtering - prioritize family-wide and actionable events
  const upcomingEvents = useMemo(() => {
    if (!familyCalendar || !Array.isArray(familyCalendar)) {
      console.log('🔍 No familyCalendar data:', { familyCalendar, isArray: Array.isArray(familyCalendar) });
      return [];
    }
    
    console.log('🔍 familyCalendar data:', familyCalendar);
    
    const next30Days = addHours(now, 30 * 24);
    console.log('🔍 Date range for filtering:', { now: now.toISOString(), next30Days: next30Days.toISOString() });
    
    // Combine calendar events and confirmed family activities
    const allEvents = [
      ...familyCalendar,
      // Include confirmed family activities that have dates
      ...(familyActivities || [])
        .filter(activity => {
          const hasDate = activity.start_date || activity.date;
          const isConfirmed = activity.confirmed_for_calendar || 
                            ['scheduled', 'Scheduled', 'onCalendar', 'has_dates'].includes(activity.status);
          return hasDate && isConfirmed;
        })
        .map(activity => ({
          ...activity,
          type: 'family-activity',
          title: activity.title || activity.name,
          date: activity.start_date || activity.date,
          dateTime: activity.start_date || activity.date
        }))
    ];
    
    console.log('🔍 All events before filtering:', allEvents);
    
    const filtered = allEvents
      .filter(event => {
        try {
          const eventDate = new Date(event.dateTime || event.date);
          console.log('🔍 Processing event:', { 
            title: event.title, 
            date: event.date, 
            dateTime: event.dateTime, 
            eventDate: eventDate.toISOString(),
            isValidDate: !isNaN(eventDate),
            isAfterNow: isAfter(eventDate, now),
            isWithin30Days: eventDate <= next30Days
          });
          
          // Strict future-only filtering
          if (isNaN(eventDate)) {
            console.log('❌ Invalid date for event:', event.title);
            return false;
          }
          
          if (!isAfter(eventDate, now)) {
            console.log('❌ Event is not after now:', event.title);
            return false;
          }
          
          if (eventDate > next30Days) {
            console.log('❌ Event is beyond 30 days:', event.title);
            return false;
          }
          
          // Filter out personal events specific to other family members
          if (event.personal_event && event.created_by !== currentUser?.id) {
            console.log('❌ Personal event for other user:', event.title);
            return false;
          }
          
          console.log('✅ Event passed all filters:', event.title);
          return true;
        } catch (err) {
          console.log('❌ Error processing event:', event.title, err);
          return false;
        }
      })
      .sort((a, b) => new Date(a.dateTime || a.date) - new Date(b.dateTime || b.date))
      .slice(0, 3); // Only show next 3 events
    
    console.log('🔍 Final filtered events:', filtered);
    return filtered;
  }, [familyCalendar, familyActivities, currentUser, now]);
  
  // Enhanced activity proposals - prioritize new ideas and pending votes
  const activityProposals = useMemo(() => {
    if (!familyActivities || !currentUser) return { newIdeas: [], pendingVotes: [] };
    
    const activeActivities = familyActivities.filter(activity => 
      !activity.confirmed_for_calendar && 
      !activity.scheduledDate && 
      activity.status !== 'archived'
    );
    
    // New activity ideas (no votes from anyone yet)
    const newIdeas = activeActivities
      .filter(activity => {
        const ratings = activity.ratings || {};
        return Object.keys(ratings).length === 0;
      })
      .slice(0, 2); // Max 2 new ideas
    
    // Activities needing current user's vote (but others have voted)
    const pendingVotes = activeActivities
      .filter(activity => {
        const ratings = activity.ratings || {};
        const hasOtherVotes = Object.keys(ratings).length > 0;
        const currentUserHasVoted = ratings[currentUser.id] !== undefined;
        return hasOtherVotes && !currentUserHasVoted;
      })
      .slice(0, 2); // Max 2 pending votes
    
    return { newIdeas, pendingVotes };
  }, [familyActivities, currentUser]);
  
  // Urgent items with smart prioritization including invites and RSVPs
  const urgentItems = useMemo(() => {
    const items = [];
    
    // Family time invites needing response (highest priority)
    const pendingInvites = familyTimeInvites
      ?.filter(invite => 
        invite.status === 'pending' && 
        (invite.invitee === currentUser?.id || invite.created_by === currentUser?.id)
      )
      ?.slice(0, 2) || [];
    
    pendingInvites.forEach(invite => {
      items.push({
        type: 'invite',
        priority: 'urgent',
        data: invite,
        title: `Family Time Invite: ${invite.activity_idea || invite.title}`,
        timeAgo: 'Pending response'
      });
    });
    
    // Activities needing RSVP for confirmed events (future events only)
    const pendingRSVPs = familyActivities
      ?.filter(activity => {
        if (!activity.confirmed_for_calendar) return false;
        const rsvps = activity.rsvps || {};
        if (rsvps[currentUser?.id] !== undefined) return false;
        
        // Check if event is in the future
        const eventDate = activity.start_date || activity.date;
        if (eventDate) {
          const eventDateTime = new Date(eventDate + 'T' + (activity.time || '23:59:59'));
          if (!isAfter(eventDateTime, new Date())) return false;
        }
        
        return true;
      })
      ?.slice(0, 2) || [];
    
    pendingRSVPs.forEach(activity => {
      items.push({
        type: 'rsvp',
        priority: 'urgent',
        data: activity,
        title: `RSVP needed: ${activity.title}`,
        timeAgo: 'Event confirmed'
      });
    });
    
    // Urgent messages
    const urgentMessages = messages
      .filter(msg => 
        (msg.type === 'to_parent' || msg.type === 'system') && 
        msg.priority === 'urgent' &&
        msg.recipients?.some(r => r.recipient_id === currentUser?.id && r.status === 'unread')
      )
      .slice(0, 2);
    
    urgentMessages.forEach(msg => {
      items.push({
        type: 'message',
        priority: 'urgent',
        data: msg,
        title: `${msg.senderName}: ${msg.content}`,
        timeAgo: format(new Date(msg.created_at), 'h:mm a')
      });
    });
    
    // Urgent shopping items
    const urgentShopping = shoppingItems
      .filter(item => item.priority === 'urgent' && isActiveShoppingItem(item))
      .slice(0, 2);
    
    urgentShopping.forEach(item => {
      items.push({
        type: 'shopping',
        priority: 'urgent',
        data: item,
        title: `Need to buy: ${item.name}`,
        timeAgo: 'Added recently'
      });
    });
    
    // Active jobs needing attention
    const activeJobs = allowanceJobs
      ?.filter(job => job.status === 'pending_approval')
      ?.slice(0, 2) || [];
    
    activeJobs.forEach(job => {
      items.push({
        type: 'allowance',
        priority: 'normal',
        data: job,
        title: `${job.kidName}: ${job.title}`,
        timeAgo: 'Pending approval'
      });
    });
    
    return items.sort((a, b) => 
      a.priority === 'urgent' && b.priority !== 'urgent' ? -1 : 
      b.priority === 'urgent' && a.priority !== 'urgent' ? 1 : 0
    );
  }, [messages, shoppingItems, allowanceJobs, familyTimeInvites, familyActivities, currentUser]);
  
  // Kids progress for family overview
  const kidsProgress = useMemo(() => {
    if (!family?.kids || !plans) return [];
    
    return family.kids.map(kid => {
      const todayPlan = plans[`${kid.id}_${today}`] || [];
      const completed = todayPlan.filter(t => t.status === 'completed').length;
      const total = todayPlan.length;
      
      return {
        ...kid,
        completed,
        total,
        percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
        hasUrgent: todayPlan.some(t => t.priority === 'urgent' && t.status !== 'completed')
      };
    });
  }, [family, plans, today]);
  
  // Helper function to switch to kid view
  const switchToKidView = (kid) => {
    console.log('Switch to kid view:', kid.name);
    
    // Clear cached data when switching users to avoid stale data
    if (user?.id) {
      localStorage.removeItem(`family-data-${user.id}`);
      localStorage.removeItem(`family-data-timestamp-${user.id}`);
    }
    
    setCurrentUser({
      ...kid,
      isParent: false,
      originalParent: true
    });
    
    // Navigate to kid dashboard
    navigate('/dashboard');
  };

  // Handle invite button click
  const handleInviteClick = () => {
    navigate('/family-invitations');
  };

  // Handle settings button click
  const handleSettingsClick = () => {
    navigate('/settings');
  };

  // Handle switch button click (switch between parent and student views)
  const handleSwitchClick = () => {
    console.log('SmartDashboard: Switch button clicked');
    console.log('SmartDashboard: currentUser:', currentUser);
    console.log('SmartDashboard: familyUsers:', familyUsers);
    
    if (!familyUsers || familyUsers.length === 0) {
      console.log('SmartDashboard: No family users available');
      return;
    }
    
    const isParentView = currentUser?.isParent || currentUser?.type === 'parent';
    
    if (isParentView) {
      // Switch to first child
      const children = familyUsers.filter(user => user.type !== 'parent');
      if (children.length > 0) {
        console.log('SmartDashboard: Switching to child:', children[0]);
        switchUser(children[0]);
      }
    } else {
      // Switch to parent
      const parent = familyUsers.find(user => user.type === 'parent');
      if (parent) {
        console.log('SmartDashboard: Switching to parent:', parent);
        switchUser(parent);
      }
    }
  };
  
  return (
    <div className="min-h-screen bg-stone-50 w-full">
      <NotificationSetup familyMemberId={currentUser?.id} />
      {/* Header - Matching mockup design */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1">Family Dashboard</h1>
              <p className="text-stone-500 text-sm sm:text-base font-medium">
                {timeContext.greeting}, {currentUser?.name || 'Sarah'}!
              </p>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              {!isMobile && (
                <>
                  <button 
                    onClick={handleInviteClick}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 border border-stone-300 text-stone-600 rounded-lg hover:bg-stone-100 transition-colors font-semibold text-sm min-h-[40px] sm:min-h-[44px]"
                  >
                    <Users size={16} />
                    Invite
                  </button>
                  <button 
                    onClick={handleSettingsClick}
                    className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 border border-stone-300 text-stone-600 rounded-lg hover:bg-stone-100 transition-colors font-semibold text-sm min-h-[40px] sm:min-h-[44px]"
                  >
                    <Settings size={16} />
                    Settings
                  </button>
                </>
              )}
              {isMobile && (
                <button 
                  onClick={handleSettingsClick}
                  className="flex items-center gap-2 px-3 py-2 border border-stone-300 text-stone-600 rounded-lg hover:bg-stone-100 transition-colors font-semibold text-sm min-h-[40px]"
                >
                  <Settings size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Smart Alert Bar - Only if urgent items exist */}
      {urgentItems.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4">
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
            <div className="flex-1 text-sm text-red-900">
              {urgentItems.length} urgent {urgentItems.length === 1 ? 'item needs' : 'items need'} your attention
            </div>
            <button 
              onClick={() => setIsUrgentItemsExpanded(!isUrgentItemsExpanded)}
              className="text-red-700 hover:text-red-800 text-sm font-medium"
            >
              {isUrgentItemsExpanded ? 'Hide' : 'View'}
            </button>
          </div>
        </div>
      )}
      
      {/* Expanded Urgent Items Section */}
      {isUrgentItemsExpanded && urgentItems.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-6">
          <div className="bg-white rounded-2xl border border-red-200 shadow-sm overflow-hidden">
            <div className="p-4 bg-red-50 border-b border-red-200">
              <h3 className="text-lg font-semibold text-red-900">Items Needing Your Attention</h3>
            </div>
            <div className="divide-y divide-red-100">
              {urgentItems.map((item, index) => (
                <UrgentItemRow key={index} item={item} navigate={navigate} />
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Main Container - Responsive layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-20 sm:pb-8">
        <div className="lg:grid lg:grid-cols-3 lg:gap-10 lg:items-start space-y-6 lg:space-y-0">
          {/* Main Content - Full width on mobile, 2 columns on desktop */}
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            {/* Big 3 Planning Block - Keep as is per requirements */}
            <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
              <div className="dashboard-big3-container">
                <UnifiedBig3Planner 
                  user={currentUser} 
                  date={new Date()} 
                  compact={isMobile}
                  isMobile={isMobile}
                  breakpoint={breakpoint}
                />
              </div>
            </div>
            
            {/* Next 3 Events */}
            <NextEventsSection events={upcomingEvents} family={family} isMobile={isMobile} />
            
            {/* Family Communications */}
            <FamilyCommunicationsSection 
              messages={urgentItems.filter(i => i.type === 'message')} 
              allMessages={messages} 
              isMobile={isMobile}
            />
          </div>
          
          {/* Sidebar - Full width on mobile, 1 column on desktop with sticky positioning */}
          <div className="space-y-6 lg:sticky lg:top-28 lg:self-start">
            {/* What's New Section */}
            <WhatsNewSection 
              isMobile={isMobile}
              userRole={isKid ? 'kid' : 'parent'}
            />
            
            {/* Notification Testing (Admin Only) */}
            {currentUser?.isParent && <NotificationTester />}
            
            {/* Shopping List Preview */}
            <ShoppingListPreview 
              shoppingItems={recentShoppingItems}
              pendingCount={pendingShoppingItems.length}
              shoppingQuickAdd={shoppingQuickAdd}
              setShoppingQuickAdd={setShoppingQuickAdd}
              handleShoppingQuickAdd={handleShoppingQuickAdd}
              isAddingItem={isAddingItem}
              shoppingInputRef={shoppingInputRef}
              family={family}
              isMobile={isMobile}
            />
            
            {/* Reading Challenge Widget - Only for kids */}
            {isKid && (
              <ReadingChallengeWidget
                activeChallenge={activeChallenge}
                currentBook={currentBook}
                readingStats={readingStats}
                isLoading={readingLoading}
                isMobile={isMobile}
              />
            )}
            
            {/* College Journey Widget - Only for kids */}
            {isKid && (
              <CollegeJourneyWidget
                isMobile={isMobile}
              />
            )}
            
            {/* Family Event Vote */}
            <FamilyEventVote 
              newIdeas={activityProposals.newIdeas}
              pendingVotes={activityProposals.pendingVotes}
              currentUser={currentUser} 
              family={family} 
              isMobile={isMobile} 
            />
            
            {/* Family Progress (Kids Summary) */}
            <FamilyProgressSummary 
              kidsProgress={kidsProgress} 
              onViewKid={currentUser?.isParent || currentUser?.type === 'parent' ? switchToKidView : null} 
              isMobile={isMobile} 
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// Next Events Section Component
const NextEventsSection = ({ events, family, isMobile }) => (
  <div className="bg-white rounded-2xl p-5 sm:p-7 border border-stone-200 shadow-sm">
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-lg sm:text-xl font-bold text-slate-900">Next 3 Events</h2>
      <Link to="/calendar" className="text-stone-500 hover:text-slate-900 font-semibold text-sm transition-colors">
        {isMobile ? 'View' : 'View Full Calendar'} <ArrowRight size={16} className="inline ml-1" />
      </Link>
    </div>
    
    <div className="space-y-4 sm:space-y-5">
      {events.length > 0 ? (
        <>
          {/* Featured Event */}
          <div className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-2xl p-4 sm:p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 sm:w-48 sm:h-48 bg-white opacity-10 rounded-full transform translate-x-12 sm:translate-x-16 -translate-y-12 sm:-translate-y-16"></div>
            <div className="relative">
              <h3 className="text-lg sm:text-xl font-bold mb-2">{events[0].title}</h3>
              <div className={`flex items-center gap-2 sm:gap-4 text-sm font-medium mb-3 opacity-95 ${isMobile ? 'flex-wrap' : ''}`}>
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {format(new Date(events[0].date + 'T00:00:00'), 'MMM d')}
                </span>
                {events[0].time && !events[0].endDate && (
                  <span className="flex items-center gap-1">
                    <Clock size={14} />
                    {events[0].time}
                  </span>
                )}
                {events[0].endDate && events[0].endDate !== events[0].date && (
                  <span className="flex items-center gap-1">
                    <Calendar size={14} />
                    {format(new Date(events[0].endDate + 'T00:00:00'), 'MMM d')}
                  </span>
                )}
                {events[0].location && (
                  <span className="flex items-center gap-1">
                    <MapPin size={14} />
                    {isMobile ? events[0].location.substring(0, 15) + (events[0].location.length > 15 ? '...' : '') : events[0].location}
                  </span>
                )}
                {/* Show creator */}
                {(() => {
                  const allMembers = [...(family?.kids || []), ...(family?.parents || [])];
                  const creator = allMembers.find(m => m.id === events[0].created_by);
                  if (creator) {
                    return (
                      <span className="flex items-center gap-1">
                        <AvatarOrInitial
                          avatar={creator.avatar}
                          name={creator.name}
                          size="xs"
                        />
                        <span className="opacity-90">{creator.name}</span>
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
              <div className="inline-flex items-center gap-2 bg-white bg-opacity-25 backdrop-blur px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold border border-white border-opacity-20">
                <CheckCircle size={14} />
                {events[0].type === 'family' ? 'Confirmed Family Event' : 'Scheduled Event'}
              </div>
            </div>
          </div>
          
          {/* Mini Events */}
          {events.slice(1, 3).map((event, index) => {
            const allMembers = [...(family?.kids || []), ...(family?.parents || [])];
            const creator = allMembers.find(m => m.id === event.created_by);
            
            return (
              <div key={event.id || index} className="bg-stone-100 rounded-xl p-4 border-l-4 border-gold-500">
                <div className="flex items-start justify-between mb-1">
                  <h4 className="font-bold text-slate-900">{event.title}</h4>
                  {creator && (
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <AvatarOrInitial
                        avatar={creator.avatar}
                        name={creator.name}
                        size="xs"
                      />
                    </div>
                  )}
                </div>
                <p className="text-sm text-stone-600 font-medium">
                  {event.endDate && event.endDate !== event.date ? (
                    // Multi-day event
                    `${format(new Date(event.date + 'T00:00:00'), 'EEE, MMM d')} - ${format(new Date(event.endDate + 'T00:00:00'), 'MMM d')}`
                  ) : event.time ? (
                    // Single day with time
                    `${format(new Date(event.date + 'T00:00:00'), 'EEE, MMM d')} at ${event.time}`
                  ) : (
                    // Single day, no time
                    format(new Date(event.date + 'T00:00:00'), 'EEE, MMM d')
                  )}
                  {event.location && ` • ${event.location}`}
                </p>
                {creator && (
                  <p className="text-xs text-stone-500 mt-1">by {creator.name}</p>
                )}
              </div>
            );
          })}
        </>
      ) : (
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <p className="text-stone-500 mb-2">No upcoming events</p>
          <p className="text-sm text-stone-400">Events scheduled for the next 7 days will appear here</p>
          <Link 
            to="/calendar" 
            className="inline-block mt-4 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors text-sm font-medium"
          >
            Add Event
          </Link>
        </div>
      )}
    </div>
  </div>
);

// Family Communications Section Component
const FamilyCommunicationsSection = ({ messages, allMessages, isMobile }) => {
  const recentMessages = allMessages
    ?.filter(msg => msg.type === 'to_parent' || msg.type === 'system')
    ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    ?.slice(0, 2) || [];

  const displayMessages = messages.length > 0 ? messages : recentMessages;

  return (
    <div className="bg-white rounded-2xl p-5 sm:p-7 border border-stone-200 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900">Family Communications</h2>
        <Link to="/messages" className="text-stone-500 hover:text-slate-900 font-semibold text-sm transition-colors">
          {isMobile ? 'View All' : 'View All'} <ArrowRight size={16} className="inline ml-1" />
        </Link>
      </div>
      
      <div className="space-y-4 sm:space-y-5">
        {displayMessages.length > 0 ? (
          displayMessages.slice(0, 2).map((item, index) => {
            const message = item.data || item;
            const isUrgent = item.priority === 'urgent' || message.priority === 'urgent';
            
            const isSystemMessage = message.type === 'system';
            
            return (
              <div key={message.id || index} className={`bg-stone-50 rounded-2xl p-4 sm:p-5 border border-stone-200 border-l-4 ${
                isUrgent ? 'border-l-red-500' : 
                isSystemMessage ? 'border-l-teal-500' : 'border-l-violet-500'
              } hover:bg-white hover:border-stone-300 transition-all`}>
                <div className="flex items-start gap-3 mb-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                    isUrgent ? 'bg-red-500' : 
                    isSystemMessage ? 'bg-teal-500' : 'bg-violet-500'
                  }`}>
                    {isSystemMessage ? <Bot className="w-4 h-4" /> : ((message.senderName || message.sender?.name)?.charAt(0) || 'U')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900">
                      {isSystemMessage ? 'System Notification' : `${message.senderName || message.sender?.name} → Parents`}
                    </div>
                    <div className="text-sm text-stone-500 font-semibold">
                      {item.timeAgo || format(new Date(message.created_at), 'h:mm a')}
                      {isUrgent && ' • Urgent'}
                    </div>
                  </div>
                </div>
                <p className="text-stone-700 mb-4 sm:mb-5 leading-relaxed">
                  {message.content}
                </p>
                {!isSystemMessage && (
                  <div className={`flex gap-3 ${isMobile ? 'flex-col sm:flex-row' : ''}`}>
                    <button className="px-4 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-950 transition-colors font-semibold text-sm min-h-[44px]">
                      Mark Done
                    </button>
                    <Link 
                      to={`/messages?reply=${message.id}`}
                      className="px-4 py-3 border border-stone-300 text-stone-600 rounded-lg hover:bg-stone-100 transition-colors font-semibold text-sm min-h-[44px] flex items-center justify-center"
                    >
                      Reply
                    </Link>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="text-center py-8">
            <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500 mb-2">No family messages</p>
            <p className="text-sm text-stone-400">Messages from family members will appear here</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Shopping List Preview Component
const ShoppingListPreview = ({ 
  shoppingItems, 
  pendingCount,
  shoppingQuickAdd, 
  setShoppingQuickAdd, 
  handleShoppingQuickAdd, 
  isAddingItem, 
  shoppingInputRef,
  family,
  isMobile
}) => (
  <div className="bg-white rounded-2xl p-5 sm:p-6 border border-stone-200 shadow-sm border-l-4 border-l-coral-500">
    <div className="flex items-center justify-between mb-5">
      <h3 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
        <ShoppingCart size={18} />
        Shopping List
        {pendingCount > 0 && (
          <span className="bg-coral-100 text-coral-700 px-2 py-0.5 rounded-full text-xs font-semibold">
            {pendingCount}
          </span>
        )}
      </h3>
      <Link to="/shopping" className="text-stone-500 hover:text-slate-900 font-semibold text-sm transition-colors">
        {isMobile ? 'View' : 'View All'} <ArrowRight size={16} className="inline ml-1" />
      </Link>
    </div>
    
    {/* Quick Add Input */}
    <form onSubmit={handleShoppingQuickAdd} className="mb-4">
      <div className="relative">
        <Search size={16} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-stone-500" />
        <input 
          ref={shoppingInputRef}
          type="text"
          value={shoppingQuickAdd}
          onChange={(e) => setShoppingQuickAdd(e.target.value)}
          placeholder="Add shopping item..."
          disabled={isAddingItem}
          className="w-full pl-12 pr-12 py-3 border border-stone-200 rounded-xl text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500 transition-colors"
        />
        <button
          type="submit"
          disabled={!shoppingQuickAdd.trim() || isAddingItem}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-coral-600 hover:text-coral-700 disabled:text-stone-300 transition-colors"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </form>
    
    <div className="space-y-4">
      {shoppingItems.length > 0 ? (
        shoppingItems.map((item, index) => {
          const requester = family?.members?.find(m => m.id === item.requested_by) || 
                           family?.parents?.find(p => p.id === item.requested_by) ||
                           family?.kids?.find(k => k.id === item.requested_by);
          
          return (
            <div key={item.id || index} className="flex items-center justify-between py-4 border-b border-stone-200 last:border-b-0">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-2 h-2 rounded-full ${
                  item.priority === 'urgent' ? 'bg-red-500' : 
                  item.priority === 'high' ? 'bg-amber-500' : 'bg-teal-500'
                }`}></div>
                <span className={`font-semibold truncate ${
                  item.status === 'purchased' ? 'text-stone-400 line-through' : 'text-slate-900'
                }`}>
                  {item.name}
                  {item.quantity > 1 && <span className="text-xs text-stone-500 ml-1">×{item.quantity}</span>}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-sm text-stone-500 font-semibold">
                  {requester?.name || item.requested_by_name || 'Family'}
                </span>
                {requester && !isMobile && (
                  <AvatarOrInitial 
                    avatar={requester.avatar}
                    name={requester.name} 
                    size="xs"
                    className="flex-shrink-0"
                  />
                )}
              </div>
            </div>
          );
        })
      ) : (
        <div className="text-center py-6">
          <ShoppingCart className="w-8 h-8 text-stone-300 mx-auto mb-2" />
          <p className="text-sm text-stone-500 mb-1">No items yet</p>
          <p className="text-xs text-stone-400">Add your first item above!</p>
        </div>
      )}
    </div>
  </div>
);

// Family Event Vote Component
const FamilyEventVote = ({ newIdeas, pendingVotes, currentUser, family, isMobile }) => {
  // Prioritize new ideas (no votes yet) over pending votes
  const allActivities = [...(newIdeas || []), ...(pendingVotes || [])];
  const newActivitiesCount = (newIdeas || []).length;
  
  if (!allActivities || allActivities.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-5 sm:p-6 border border-stone-200 shadow-sm border-l-4 border-l-amber-500">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base sm:text-lg font-bold text-slate-900">Family Activity Ideas</h3>
          <span className="px-3 sm:px-4 py-1 bg-stone-100 text-stone-600 rounded-full text-sm font-semibold">All Caught Up</span>
        </div>
        
        <div className="text-center py-6">
          <Star className="w-8 h-8 text-stone-300 mx-auto mb-2" />
          <p className="text-sm text-stone-500 mb-1">No new activities to vote on</p>
          <p className="text-xs text-stone-400">New activity ideas will appear here</p>
          <Link 
            to="/family" 
            className="inline-block mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
          >
            Browse Activities
          </Link>
        </div>
      </div>
    );
  }

  const activity = allActivities[0];
  const isNewIdea = (newIdeas || []).includes(activity);
  const familyRatings = Object.values(activity.ratings || {});
  const averageRating = familyRatings.length > 0 
    ? (familyRatings.reduce((sum, rating) => sum + rating, 0) / familyRatings.length).toFixed(1)
    : 0;

  return (
    <div className={`rounded-2xl p-5 sm:p-6 border shadow-sm border-l-4 ${
      isNewIdea 
        ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200 border-l-emerald-500' 
        : 'bg-white border-stone-200 border-l-amber-500'
    }`}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <h3 className="text-base sm:text-lg font-bold text-slate-900">
            {isNewIdea ? 'New Activity Idea!' : 'Family Activity Vote'}
          </h3>
          {newActivitiesCount > 1 && (
            <span className="px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full text-xs font-medium">
              +{newActivitiesCount - 1} more
            </span>
          )}
        </div>
        <span className={`px-3 sm:px-4 py-1 rounded-full text-sm font-semibold ${
          isNewIdea 
            ? 'bg-emerald-500 text-white animate-pulse' 
            : 'bg-amber-500 text-white'
        }`}>
          {isNewIdea ? 'NEW!' : 'Vote'}
        </span>
      </div>
      
      {activity.image ? (
        <img 
          src={activity.image} 
          alt={activity.title}
          className={`w-full ${isMobile ? 'h-24' : 'h-32'} object-cover rounded-xl mb-5 border border-stone-200`}
        />
      ) : (
        <div className={`w-full ${isMobile ? 'h-24' : 'h-32'} bg-stone-100 rounded-xl mb-5 border border-stone-200 flex items-center justify-center`}>
          <Target className="w-8 h-8 text-stone-400" />
        </div>
      )}
      
      <h4 className="text-base sm:text-lg font-bold text-slate-900 mb-2">{activity.title}</h4>
      <p className="text-stone-500 mb-4 text-sm">{activity.description || 'Vote on this family activity'}</p>
      
      <div className={`flex items-center justify-between ${isMobile ? 'flex-col gap-4' : ''}`}>
        <div className={isMobile ? 'text-center' : ''}>
          <div className="text-sm text-stone-500 font-semibold mb-2">
            Family Average: <strong className="text-slate-900">{averageRating}</strong>
          </div>
          <div className="w-20 h-2 bg-stone-200 rounded-full mx-auto">
            <div 
              className="h-2 bg-amber-500 rounded-full transition-all"
              style={{ width: `${(averageRating / 10) * 100}%` }}
            />
          </div>
        </div>
        <div className={`text-${isMobile ? 'center' : 'right'}`}>
          <div className="text-2xl font-extrabold text-slate-900">
            {activity.ratings?.[currentUser?.id] || '?'}
          </div>
          <div className="text-sm text-stone-500 font-semibold">Your Rating</div>
        </div>
      </div>
      
      <div className="mt-4">
        <ActivityRatingSlider
          activity={activity}
          currentUser={currentUser}
          family={family}
          compact={true}
        />
      </div>
      
      {/* Enhanced call-to-action for new activities */}
      {isNewIdea && (
        <div className="mt-4 p-3 bg-emerald-100 border border-emerald-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">
                Be the first to vote on this new activity!
              </span>
            </div>
            <Link 
              to="/family" 
              className="px-3 py-1 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            >
              Vote Now
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

// Urgent Item Row Component
const UrgentItemRow = ({ item, navigate }) => {
  const getItemIcon = (type) => {
    switch (type) {
      case 'invite':
        return <Users className="w-4 h-4 text-azure-600" />;
      case 'rsvp':
        return <Calendar className="w-4 h-4 text-violet-600" />;
      case 'message':
        return <MessageSquare className="w-4 h-4 text-emerald-600" />;
      case 'shopping':
        return <ShoppingCart className="w-4 h-4 text-orange-600" />;
      case 'allowance':
        return <DollarSign className="w-4 h-4 text-teal-600" />;
      default:
        return <AlertCircle className="w-4 h-4 text-red-600" />;
    }
  };

  const getActionPath = (item) => {
    switch (item.type) {
      case 'invite':
        return '/family';
      case 'rsvp':
        return '/family';
      case 'message':
        return '/messages';
      case 'shopping':
        return '/shopping';
      case 'allowance':
        return '/allowance';
      default:
        return '/';
    }
  };

  const getActionText = (item) => {
    switch (item.type) {
      case 'invite':
        return 'Respond';
      case 'rsvp':
        return 'RSVP';
      case 'message':
        return 'Reply';
      case 'shopping':
        return 'View';
      case 'allowance':
        return 'Review';
      default:
        return 'View';
    }
  };

  const handleClick = () => {
    navigate(getActionPath(item));
  };

  return (
    <div className="p-4 hover:bg-red-50 transition-colors cursor-pointer" onClick={handleClick}>
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0">
          {getItemIcon(item.type)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900">{item.title}</p>
          <p className="text-xs text-stone-600 mt-1">{item.timeAgo}</p>
        </div>
        <div className="flex-shrink-0">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full hover:bg-red-200 transition-colors"
          >
            {getActionText(item)}
          </button>
        </div>
      </div>
    </div>
  );
};

// Family Progress Summary Component - Compact design with responsive grid
const FamilyProgressSummary = ({ kidsProgress, onViewKid, isMobile }) => (
  <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm">
    <h3 className="text-lg font-bold text-slate-900 mb-5">Family Progress</h3>
    
    <div className={`grid gap-4 ${
      isMobile 
        ? 'grid-cols-2' 
        : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
    }`} style={{ gridTemplateColumns: isMobile ? undefined : 'repeat(auto-fit, minmax(120px, 1fr))' }}>
      {kidsProgress.length > 0 ? (
        kidsProgress.map(kid => {
          const KidCard = ({ children }) => onViewKid ? (
            <button 
              key={kid.id} 
              onClick={() => onViewKid(kid)}
              className="relative bg-white rounded-2xl p-4 border border-stone-200 hover:border-teal-300 hover:shadow-sm transition-all text-center group w-full"
            >
              {children}
            </button>
          ) : (
            <div 
              key={kid.id} 
              className="relative bg-white rounded-2xl p-4 border border-stone-200 text-center w-full"
            >
              {children}
            </div>
          );

          return (
            <KidCard key={kid.id}>
              {/* Warning icon for urgent tasks */}
              {kid.hasUrgent && (
                <div className="absolute top-3 right-3 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-3 h-3 text-white" />
                </div>
              )}
              
              {/* Eye icon for view switching - only shows for parents on hover */}
              {onViewKid && (
                <div className="absolute top-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                    <Eye className="w-3 h-3 text-white" />
                  </div>
                </div>
              )}
              
              {/* Avatar */}
              <div className="mb-4">
                <AvatarOrInitial 
                  avatar={kid.avatar}
                  name={kid.name} 
                  size="lg"
                  className="mx-auto"
                />
              </div>
              
              {/* Name */}
              <h4 className="font-semibold text-slate-900 mb-2 truncate">{kid.name}</h4>
              
              {/* Progress */}
              <p className="text-sm text-stone-600 mb-1">
                {kid.completed}/{kid.total} done
              </p>
              
              {/* Status */}
              <p className="text-xs text-stone-500">
                {kid.total === 0 ? 'No tasks set' :
                 kid.hasUrgent ? 'Has urgent task' :
                 kid.percentage === 100 ? 'All complete!' :
                 kid.percentage > 50 ? 'Good progress' : 
                 kid.completed === 0 ? 'Not started' : 'In progress'}
              </p>
            </KidCard>
          );
        })
      ) : (
        <div className="col-span-full text-center py-8">
          <Users className="w-8 h-8 text-stone-300 mx-auto mb-3" />
          <p className="text-sm text-stone-500 mb-1">No family members yet</p>
          <p className="text-xs text-stone-400">Add family members to see their progress</p>
        </div>
      )}
    </div>
  </div>
);